import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Sparkles, Settings2, Trash2, Plus, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { DEMO_MATCHES, MARKET_GROUPS, ALL_COLS } from "@/constants/giocataDemo";

const emptyOdds = () => ALL_COLS.reduce((a, c) => ({ ...a, [c]: "" }), {});

const fromApiMatches = (matches) => (matches || []).map((m, i) => ({
  match_id: m.match_id || `m${i + 1}`,
  home: m.home || "",
  away: m.away || "",
  date: m.date || "",
  time: m.time || "",
  odds: ALL_COLS.reduce((a, c) => ({ ...a, [c]: m.odds?.[c] != null ? String(m.odds[c]) : "" }), {}),
}));

const fromTemplate = () => DEMO_MATCHES.map((m) => ({
  match_id: m.id,
  home: m.home,
  away: m.away,
  date: m.date,
  time: m.time,
  odds: ALL_COLS.reduce((a, c) => ({ ...a, [c]: m.odds[c] != null ? String(m.odds[c]) : "" }), {}),
}));

// Pannello admin: imposta le quote della griglia per la stanza.
export default function GridOddsAdmin({ roomId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef(null);
  // Bumped whenever the admin edits rows; an in-flight load() with a stale
  // token is ignored so it can't overwrite freshly-set rows (race fix).
  const editToken = useRef(0);

  const load = useCallback(async () => {
    const token = editToken.current;
    setLoadingRows(true);
    try {
      const g = await api(`/rooms/${roomId}/grid`);
      if (token !== editToken.current) return; // stale — admin already edited
      if (g.matches?.length) {
        setRows(g.matches.map((m) => ({
          match_id: m.match_id,
          home: m.home, away: m.away, date: m.date || "", time: m.time || "",
          odds: ALL_COLS.reduce((a, c) => ({ ...a, [c]: m.odds?.[c] != null ? String(m.odds[c]) : "" }), {}),
        })));
      }
    } catch (e) { /* stanza senza griglia: ok */ }
    finally { if (token === editToken.current) setLoadingRows(false); }
  }, [roomId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const touch = () => { editToken.current += 1; setLoadingRows(false); };
  const setCell = (i, col, v) => { touch(); setRows((r) => r.map((row, idx) => idx === i ? { ...row, odds: { ...row.odds, [col]: v } } : row)); };
  const setField = (i, k, v) => { touch(); setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row)); };
  const addRow = () => { touch(); setRows((r) => [...r, { match_id: `m${Date.now()}`, home: "", away: "", date: "", time: "", odds: emptyOdds() }]); };
  const delRow = (i) => { touch(); setRows((r) => r.filter((_, idx) => idx !== i)); };
  const loadTemplate = () => { touch(); setRows(fromTemplate()); };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Immagine troppo grande (max 8MB)"); return; }
    setParsing(true);
    touch();
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const out = await api(`/rooms/${roomId}/grid/odds/from-image`, { method: "POST", body: { image_base64: b64 } });
      const parsed = fromApiMatches(out.matches);
      if (parsed.length === 0) { toast.error(out.note || "Nessuna partita riconosciuta nella foto"); return; }
      touch();
      setRows(parsed);
      toast.success(`${parsed.length} partite riconosciute — controlla le quote e salva`);
    } catch (err) { toast.error(err.message); }
    finally { setParsing(false); }
  };

  const save = async () => {
    const matches = rows
      .filter((r) => r.home.trim() && r.away.trim())
      .map((r) => ({
        match_id: r.match_id,
        home: r.home.trim(), away: r.away.trim(), date: r.date || null, time: r.time || null,
        odds: ALL_COLS.reduce((a, c) => {
          const n = parseFloat(String(r.odds[c]).replace(",", "."));
          if (!isNaN(n) && n > 1.0) a[c] = n;
          return a;
        }, {}),
      }));
    if (matches.length === 0) { toast.error("Inserisci almeno una partita valida."); return; }
    setBusy(true);
    try {
      await api(`/rooms/${roomId}/grid/odds`, { method: "POST", body: { matches } });
      toast.success(`Quote salvate · ${matches.length} partite`);
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid="grid-admin-open" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 border border-[#F59E0B]/40 bg-[#F59E0B]/5 text-[#F59E0B] rounded-xl py-2.5 text-sm font-bold hover:bg-[#F59E0B]/10">
        <Settings2 size={16} /> Gestione quote griglia (admin)
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-[#F59E0B]/40 bg-[#181D22] p-4 space-y-3" data-testid="grid-admin-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-extrabold flex items-center gap-2"><Settings2 size={16} className="text-[#F59E0B]" /> Gestione quote griglia</div>
        <button data-testid="grid-admin-close" onClick={() => setOpen(false)} className="text-xs text-[#94A3B8] hover:text-white">Chiudi</button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button data-testid="grid-admin-photo" onClick={() => fileRef.current?.click()} disabled={parsing} className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#F59E0B] text-[#1A1000] rounded-md px-3 py-1.5 disabled:opacity-50"><Camera size={13} /> {parsing ? "Analisi foto..." : "Carica foto griglia (AI)"}</button>
        <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="grid-admin-photo-input" />
        <button data-testid="grid-admin-template" onClick={loadTemplate} className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/10 hover:bg-white/15 rounded-md px-3 py-1.5"><Sparkles size={13} className="text-[#F59E0B]" /> Carica quote di esempio (9 eventi Serie A)</button>
        <button data-testid="grid-admin-add" onClick={addRow} className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/10 hover:bg-white/15 rounded-md px-3 py-1.5"><Plus size={13} /> Aggiungi partita</button>
      </div>
      {loadingRows && <p className="text-xs text-[#F59E0B]">Caricamento quote esistenti...</p>}
      <p className="text-xs text-[#64748B]">Lascia vuota una casella per bloccare quel mercato (mostra il lucchetto). "Carica foto griglia (AI)" legge lo screenshot del bookmaker e compila le quote: controllale sempre prima di salvare.</p>

      <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full text-xs border-collapse min-w-[700px]">
          <thead>
            <tr className="text-[10px] uppercase text-[#64748B]">
              <th className="text-left px-2 py-1">Partita</th>
              {ALL_COLS.map((c) => <th key={c} className="px-1 py-1 text-center">{c}</th>)}
              <th />
              <th className="w-3" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.match_id} className="border-t border-white/5">
                <td className="px-2 py-1.5 align-top min-w-[128px]">
                  <input value={r.home} onChange={(e) => setField(i, "home", e.target.value)} placeholder="Casa" className="w-full bg-[#0F1216] border border-white/10 rounded px-1.5 py-1 mb-1 text-white" />
                  <input value={r.away} onChange={(e) => setField(i, "away", e.target.value)} placeholder="Trasferta" className="w-full bg-[#0F1216] border border-white/10 rounded px-1.5 py-1 mb-1 text-white" />
                  <div className="flex gap-1">
                    <input value={r.date} onChange={(e) => setField(i, "date", e.target.value)} placeholder="gg/mm" className="w-1/2 bg-[#0F1216] border border-white/10 rounded px-1.5 py-1 text-[#94A3B8]" />
                    <input value={r.time} onChange={(e) => setField(i, "time", e.target.value)} placeholder="hh:mm" className="w-1/2 bg-[#0F1216] border border-white/10 rounded px-1.5 py-1 text-[#94A3B8]" />
                  </div>
                </td>
                {ALL_COLS.map((c) => (
                  <td key={c} className="px-1 py-1.5 align-top">
                    <input
                      data-testid={`grid-admin-odd-${i}-${c}`}
                      value={r.odds[c]}
                      onChange={(e) => setCell(i, c, e.target.value)}
                      inputMode="decimal"
                      placeholder="—"
                      className="w-12 text-center bg-[#0F1216] border border-white/10 rounded px-1 py-1 text-white"
                    />
                  </td>
                ))}
                <td className="px-1 py-1.5 align-top">
                  <button onClick={() => delRow(i)} className="p-1 text-[#EF4444] hover:bg-[#EF4444]/15 rounded"><Trash2 size={14} /></button>
                </td>
                <td className="w-3" aria-hidden />
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={ALL_COLS.length + 3} className="px-2 py-4 text-center text-[#64748B]">Nessuna partita. Carica l'esempio o aggiungi manualmente.</td></tr>}
          </tbody>
        </table>
      </div>

      <button data-testid="grid-admin-save" onClick={save} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-[#F59E0B] text-[#1A1000] font-bold rounded-md py-2.5 disabled:opacity-50">
        <Save size={16} /> {busy ? "Salvataggio..." : "Salva quote"}
      </button>
    </div>
  );
}
